// Charger anomaly + broken-meter console (maintenance).
// Base = /api/maintenance/anomalies.
//
// Written by backend-stream-processor-go, which watches the OCPP MeterValues
// and StatusNotification streams out of band. Severity drives what happens:
//
//   info      recorded only, never mailed (an idle phase reading high is
//             normal; the fleet produces tens of thousands of them)
//   warning   mailed once when the condition starts and once when it clears
//   critical  mailed immediately, and the only tier that may stop a session
//
// Only hardware hazards ever stop a session. Supply conditions — over and
// under voltage — are recorded and alerted but never actioned, because the
// charger protects itself and pulling the plug for a grid condition would turn
// the safety net into the outage.
import axiosInstance from "../axiosInstance";

const BASE = "/api/maintenance/anomalies";

export type Severity = "info" | "warning" | "critical";

export interface Anomaly {
  id: number;
  anomaly_guid: string;
  device_ocpp_id: string;
  connector_number: number | null;
  station_name: string;
  charging_station_guid: string;
  kind: string;
  severity: Severity;
  state: string;
  observed_value: number | null;
  limit_value: number | null;
  unit: string | null;
  limit_source: string | null;
  charger_error_code: string | null;
  breach_count: number;
  probe_count: number;
  probe_breach_count: number;
  detail: string | null;
  first_seen_at: string;
  last_seen_at: string;
  confirmed_at: string | null;
  cleared_at: string | null;
  notified_at: string | null;
  stop_state: string;
  reset_state: string;
  action_detail: string | null;
  session_guid: string | null;
  duration_seconds: number;
}

export interface MeterFault {
  id: number;
  device_ocpp_id: string;
  connector_number: number | null;
  station_name: string;
  session_guid: string;
  fault_kind: string;
  /**
   * How the energy was recovered, and therefore how much to trust it.
   * `register_delta` is exact metered energy reassembled from the charger's own
   * increments; `power_active` / `volt_amp` / `nominal_volt_amp` are integrated
   * estimates used only when the register is unusable.
   */
  energy_source: string;
  billed_wh: number;
  recovered_wh: number;
  unbilled_wh: number;
  rate_kwh: number | null;
  unbilled_value: number | null;
  billing_applied: boolean;
  sample_count: number;
  register_regressions: number;
  detected_at: string;
  session_ended_at: string | null;
  restart_state: string;
  restart_sent_at: string | null;
}

export interface AnomalySummary {
  window_days: number;
  anomalies: {
    open_total: number;
    open_critical: number;
    open_warning: number;
    sessions_stopped: number;
    chargers_reset: number;
    resets_deferred: number;
    devices_affected: number;
    total_in_window: number;
  };
  meter_faults: {
    faults: number;
    devices_affected: number;
    recovered_kwh: number;
    unbilled_kwh: number;
    unbilled_value: number;
    billed_at_recovered_rate: number;
    chargers_reset: number;
    resets_deferred: number;
  };
  by_device: Array<{
    device_ocpp_id: string;
    station_name: string;
    anomalies: number;
    critical: number;
    warning: number;
    open_now: number;
    total_breaches: number;
    kinds: string;
    last_seen_at: string;
  }>;
}

export interface ErrorCodePolicy {
  error_code: string;
  severity: Severity;
  /**
   * Whether this code may ever justify an emergency stop + hard reset. It can
   * only NARROW — the processor still requires critical severity — so setting
   * it on a low-severity code arms nothing.
   */
  actionable: boolean;
  description: string | null;
  updated_at: string | null;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AnomalyFilters {
  severity?: Severity | "";
  kind?: string;
  device?: string;
  open_only?: boolean;
  days?: number;
  page?: number;
  page_size?: number;
}

export const anomaliesApi = {
  list: (f: AnomalyFilters = {}) =>
    axiosInstance.get<Paged<Anomaly>>(BASE, {
      params: {
        severity: f.severity || undefined,
        kind: f.kind || undefined,
        device: f.device || undefined,
        open_only: f.open_only,
        days: f.days ?? 7,
        page: f.page ?? 1,
        page_size: f.page_size ?? 50,
      },
    }),

  summary: (days = 7, topDevices = 10) =>
    axiosInstance.get<AnomalySummary>(`${BASE}/summary`, {
      params: { days, top_devices: topDevices },
    }),

  meterFaults: (days = 30, device = "", page = 1, pageSize = 50) =>
    axiosInstance.get<Paged<MeterFault>>(`${BASE}/meter-faults`, {
      params: { days, device: device || undefined, page, page_size: pageSize },
    }),

  errorCodePolicy: () => axiosInstance.get<ErrorCodePolicy[]>(`${BASE}/error-code-policy`),

  /**
   * Retune one error code. Takes effect within about 5 minutes — the processor
   * reloads the table on a timer — so no deploy and no restart. Audited.
   */
  updateErrorCodePolicy: (
    code: string,
    body: { severity: Severity; actionable: boolean; description?: string | null },
  ) => axiosInstance.put<ErrorCodePolicy>(`${BASE}/error-code-policy/${encodeURIComponent(code)}`, body),
};

export default anomaliesApi;
